import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { RouteProp, useRoute } from '@react-navigation/native';
import { useAuth } from '../context/AuthContext';
import { useTheme, ThemeColors } from '../context/ThemeContext';
import { Toast } from '../components/common/Toast';
import { WebPageShell } from '../components/common/WebPageShell';
import { DocumentsContent } from '../components/documents/DocumentsContent';
import { RootStackParamList } from '../types';

/** Request certificates, follow requests and keep issued documents (web). */
export function DocumentsScreen() {
  const route = useRoute<RouteProp<RootStackParamList, 'Documents'>>();
  const { selectedStudent } = useAuth();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const notify = useCallback((message: string, type: 'success' | 'error') => setToast({ message, type }), []);

  return (
    <WebPageShell title="Documents" maxWidth={900}>
      <View style={[styles.header, isMobile && styles.headerMobile]}>
        <Text style={styles.title} accessibilityRole="header">
          Documents
        </Text>
        <Text style={styles.subtitle}>
          {selectedStudent
            ? `Request certificates for ${selectedStudent.name} and download issued documents.`
            : 'Request certificates and download issued documents.'}
        </Text>
      </View>

      <View style={styles.panel}>
        <DocumentsContent initialTab={route.params?.tab} scrollable={false} notify={notify} />
      </View>

      <Toast
        visible={!!toast}
        message={toast?.message ?? ''}
        type={toast?.type ?? 'success'}
        onDismiss={() => setToast(null)}
      />
    </WebPageShell>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    header: { marginBottom: 16 },
    headerMobile: { paddingHorizontal: 6, paddingTop: 6 },
    title: { fontSize: 22, fontWeight: '800', color: colors.textPrimary },
    subtitle: { fontSize: 13, color: colors.textSecondary, marginTop: 4 },
    panel: { borderRadius: 12, overflow: 'hidden' },
  });
